import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/common/Layout';
import { authAPI } from '../services/api';
import { auth } from '../services/auth';
import { AlertCircle, Copy, Check } from 'lucide-react';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const navigate = useNavigate();
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const demoCredentials = {
    email: 'user@example.com',
    password: 'P@ssw0rd'
  };

  const handleSubmit = async e => {
    e.preventDefault();
    try {
      const res = await authAPI.login({ email, password });
      auth.setToken(res.data.token);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed');
    }
  };

  const fillDemoCredentials = () => {
    setEmail(demoCredentials.email);
    setPassword(demoCredentials.password);
    setError('');
  };

  const copyToClipboard = () => {
    const credentialsText = `Email: ${demoCredentials.email}\nPassword: ${demoCredentials.password}`;
    navigator.clipboard.writeText(credentialsText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Layout title="Login">
      <div className="max-w-md mx-auto mt-10">
        {/* Demo Info Box */}
        <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle size={20} className="text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-blue-900 mb-2">Demo Credentials</h3>
              <div className="space-y-1 text-sm text-blue-800 mb-3 font-mono bg-white p-3 rounded border border-blue-100">
                <div className="text-gray-700">
                  <span className="font-semibold">Email:</span> {demoCredentials.email}
                </div>
                <div className="text-gray-700">
                  <span className="font-semibold">Password:</span> {demoCredentials.password}
                </div>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={fillDemoCredentials}
                  className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 transition-colors font-medium"
                >
                  Auto-fill
                </button>
                <button
                  type="button"
                  onClick={copyToClipboard}
                  className="px-3 py-2 bg-blue-100 text-blue-600 text-sm rounded hover:bg-blue-200 transition-colors flex items-center gap-1 font-medium"
                >
                  {copied ? (
                    <>
                      <Check size={16} />
                      Copied
                    </>
                  ) : (
                    <>
                      <Copy size={16} />
                      Copy
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Login Form */}
        <div className="bg-white border border-gray-200 rounded-xl p-8 shadow-sm">
          <h2 className="text-2xl font-bold mb-6 text-gray-900">Welcome Back</h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
                <AlertCircle size={16} />
                {error}
              </div>
            )}
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email
              </label>
              <input
                type="email"
                placeholder="user@example.com"
                value={email}
                onChange={e => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                required
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password
              </label>
              <input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent transition-all"
                required
              />
            </div>

            <button 
              type="submit" 
              className="w-full px-4 py-2.5 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium"
            >
              Login
            </button>
          </form>
        </div>

        {/* Footer text */}
        <p className="text-center text-sm text-gray-600 mt-6">
          Don't have an account? Contact support
        </p>
      </div>
    </Layout>
  );
}